'use client';

import { useContext, useEffect, useMemo, useState } from 'react';
import { SessionContext } from '@benzinga/session-context';
import { useWatchlistContext } from '@benzinga/user-context';
import { DelayedQuote, QuotesManager } from '@benzinga/quotes-manager';
import { logger } from '@benzinga/logging';
import { normalizeWatchlistSymbol } from '@benzinga/watchlist-manager';

import { fetchDelayedQuotesWithRetry } from '../../../edge/dashboard/components/watchlistOverviewStats';
import { publishPortfolioQuoteBar, resetPortfolioQuoteBar } from '../../../../_libs/site-shell/portfolioQuoteBar';
import { writePortfolioShellSnapshot } from './portfolioShellSnapshot';

const MAX_MOVER_TICKERS = 8;

export type PortfolioMover = {
  changePercent: number;
  lastTradePrice: number;
  logo: string | null;
  name: string;
  symbol: string;
};

export const usePortfolioQuoteBar = (): {
  isLoadingMovers: boolean;
  movers: PortfolioMover[];
} => {
  const { isLoadingWatchlists, overviewWatchlist } = useWatchlistContext();
  const session = useContext(SessionContext);
  const [movers, setMovers] = useState<PortfolioMover[]>([]);
  const [isLoadingMovers, setIsLoadingMovers] = useState(true);

  const overviewSymbolsKey = overviewWatchlist?.symbols.map(symbol => symbol.symbol).join(',') ?? '';
  const symbolsKey = useMemo(() => {
    if (!overviewSymbolsKey) return '';
    return [...new Set(overviewSymbolsKey.split(','))].join(',');
  }, [overviewSymbolsKey]);

  useEffect(() => {
    if (!symbolsKey) {
      if (!isLoadingWatchlists) {
        publishPortfolioQuoteBar(null);
        writePortfolioShellSnapshot({ quoteBarTickers: [] });
        setMovers([]);
        setIsLoadingMovers(false);
      }
      return;
    }

    const symbolList = symbolsKey.split(',');
    const initialTickers = symbolList.slice(0, MAX_MOVER_TICKERS);
    publishPortfolioQuoteBar({ tickers: initialTickers });
    writePortfolioShellSnapshot({ quoteBarTickers: initialTickers });
    setIsLoadingMovers(true);

    const abortController = new AbortController();

    const loadMovers = async () => {
      try {
        const quoteManager = session.getSession().getManager(QuotesManager);
        const quotesResult = await fetchDelayedQuotesWithRetry(
          () => quoteManager.getDelayedQuotes(symbolList),
          symbolList.length,
        );
        if (abortController.signal.aborted) return;

        const quoteBySymbol = new Map<string, DelayedQuote>();
        Object.values(quotesResult?.ok ?? {}).forEach(quote => {
          if (!quote) return;
          quoteBySymbol.set(quote.symbol, quote);
          quoteBySymbol.set(normalizeWatchlistSymbol(quote.symbol), quote);
        });

        const resolveQuote = (symbol: string) =>
          quoteBySymbol.get(symbol) ?? quoteBySymbol.get(normalizeWatchlistSymbol(symbol));

        const initialQuotes = Object.fromEntries(
          initialTickers
            .map(symbol => {
              const quote = resolveQuote(symbol);
              return quote ? ([quote.symbol, quote] as const) : null;
            })
            .filter((entry): entry is readonly [string, DelayedQuote] => Boolean(entry)),
        );

        const matchedQuotes = symbolList.map(resolveQuote).filter((quote): quote is DelayedQuote => Boolean(quote));

        const uniqueBySymbol = new Map<string, DelayedQuote>();
        (matchedQuotes.length > 0 ? matchedQuotes : [...quoteBySymbol.values()]).forEach(quote => {
          if (!uniqueBySymbol.has(quote.symbol)) uniqueBySymbol.set(quote.symbol, quote);
        });

        const ranked = [...uniqueBySymbol.values()].sort(
          (left, right) => Math.abs(right.changePercent ?? 0) - Math.abs(left.changePercent ?? 0),
        );

        const tapeMovers = ranked.slice(0, MAX_MOVER_TICKERS);
        const tapeQuotes =
          Object.keys(initialQuotes).length > 0
            ? initialQuotes
            : Object.fromEntries(tapeMovers.map(quote => [quote.symbol, quote]));

        if (Object.keys(tapeQuotes).length > 0) {
          const tickers = tapeMovers.length > 0 ? tapeMovers.map(quote => quote.symbol) : initialTickers;
          publishPortfolioQuoteBar({
            quotes: tapeQuotes,
            tickers,
          });
          writePortfolioShellSnapshot({ quoteBarTickers: tickers });
        }

        setMovers(
          ranked.slice(0, 3).map(quote => ({
            changePercent: quote.changePercent ?? 0,
            lastTradePrice: quote.lastTradePrice ?? 0,
            logo: null,
            name: quote.companyStandardName || quote.name || quote.symbol,
            symbol: quote.symbol,
          })),
        );

        const topThree = ranked.slice(0, 3);
        if (topThree.length > 0) {
          try {
            const logosReq = await quoteManager.getQuotesLogosMapped(topThree.map(quote => quote.symbol));
            if (!abortController.signal.aborted && logosReq.ok) {
              const logos = logosReq.ok;
              setMovers(
                topThree.map(quote => ({
                  changePercent: quote.changePercent ?? 0,
                  lastTradePrice: quote.lastTradePrice ?? 0,
                  logo:
                    logos[quote.symbol]?.files?.['mark_composite_light'] ??
                    logos[quote.symbol]?.files?.['mark_composite_dark'] ??
                    null,
                  name: quote.companyStandardName || quote.name || quote.symbol,
                  symbol: quote.symbol,
                })),
              );
            }
          } catch (logoError) {
            logger.error('portfolio quote bar logos', logoError);
          }
        }

        if (tapeMovers.length === 0) return;

        const rankedTickers = tapeMovers.map(quote => quote.symbol);
        const orderChanged = rankedTickers.some((symbol, index) => symbol !== initialTickers[index]);
        if (!orderChanged) return;

        window.setTimeout(() => {
          if (abortController.signal.aborted) return;
          publishPortfolioQuoteBar({
            quotes: Object.fromEntries(tapeMovers.map(quote => [quote.symbol, quote])),
            tickers: rankedTickers,
          });
          writePortfolioShellSnapshot({ quoteBarTickers: rankedTickers });
        }, 220);
      } catch (error) {
        logger.error('portfolio quote bar movers', error);
        if (!abortController.signal.aborted) setMovers([]);
      } finally {
        if (!abortController.signal.aborted) setIsLoadingMovers(false);
      }
    };

    loadMovers();

    return () => {
      abortController.abort();
    };
  }, [isLoadingWatchlists, session, symbolsKey]);

  useEffect(() => {
    return () => {
      resetPortfolioQuoteBar();
    };
  }, []);

  return { isLoadingMovers: isLoadingWatchlists || isLoadingMovers, movers };
};
